import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { RiSearch2Line } from "react-icons/ri";
import { useCallback, useEffect, useRef, useState } from "react";
import useAddressContext from "../../hooks/useAddressContext";
import toast from "react-hot-toast";

const PlaceAutocompleteForm = () => {
  const { setSuggestAddress, setSuggestCordinate, setSuggestPostalCode } =
    useAddressContext();

  const placesLib = useMapsLibrary("places");
  const [inputValue, setInputValue] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const sessionToken = useRef(null);
  const autocompleteService = useRef(null);

  // Initialize the autocomplete service and session token
  useEffect(() => {
    if (!placesLib) return;

    const initializeService = () => {
      autocompleteService.current = new placesLib.AutocompleteService();
      sessionToken.current = new placesLib.AutocompleteSessionToken();
      setIsReady(true);
    };

    initializeService();

    return () => {
      // Cleanup
      autocompleteService.current = null;
      sessionToken.current = null;
    };
  }, [placesLib]);

  // Fetch predictions when input changes
  useEffect(() => {
    if (!isReady || !inputValue.trim()) {
      setPredictions([]);
      return;
    }

    const getPredictions = async () => {
      try {
        const request = {
          input: inputValue,
          sessionToken: sessionToken.current,
          types: ["geocode"], // Restrict to geographic locations
          componentRestrictions: { country: "gb" }, // Optional: restrict to specific country
        };

        const result = await autocompleteService.current.getPlacePredictions(
          request
        );
        setPredictions(result.predictions || []);
      } catch (error) {
        console.error("Autocomplete error:", error);
        setPredictions([]);
      }
    };

    const debounceTimer = setTimeout(getPredictions, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue, isReady]);

  // Handle place selection
  const handleSelect = useCallback(
    async (placeId, description) => {
      if (!placesLib) return;

      try {
        // Clear predictions immediately when an item is selected
        setPredictions([]);
        setInputValue(description);

        // Create a new Places Service instance
        const placesService = new placesLib.PlacesService(
          document.createElement("div")
        );

        // Get place details
        const request = {
          placeId,
          fields: ["geometry", "formatted_address", "address_components"],
          sessionToken: sessionToken.current,
        };

        const placeDetails = await new Promise((resolve, reject) => {
          placesService.getDetails(request, (place, status) => {
            if (status === placesLib.PlacesServiceStatus.OK) {
              resolve(place);
            } else {
              reject(new Error(`Place details error: ${status}`));
            }
          });
        });

        // Update context with selected place data
        if (placeDetails) {
          const { lat, lng } = placeDetails.geometry.location;
          setSuggestCordinate({ lat: lat(), lng: lng() });
          setSuggestAddress(placeDetails.formatted_address);

          // Extract postal code
          const postalComponent = placeDetails.address_components.find((comp) =>
            comp.types.includes("postal_code")
          );

          if (postalComponent) {
            setSuggestPostalCode(postalComponent.long_name);
          } else {
            toast.error(
              "Could not find postal code for this location. Please select a more specific address."
            );
            setSuggestPostalCode("");
          }
        }

        // Generate a new session token for the next request
        sessionToken.current = new placesLib.AutocompleteSessionToken();
      } catch (error) {
        console.error("Place details error:", error);
        toast.error("Failed to get place details. Please try again.");
      }
    },
    [placesLib, setSuggestAddress, setSuggestCordinate, setSuggestPostalCode]
  );

  return (
    <form className="space-y-5 py-5 w-full relative">
      <div className="flex bg-secondary/20 items-center p-4 rounded-md">
        <RiSearch2Line />
        <input
          className="bg-transparent cursor-pointer w-full placeholder:text-sm placeholder:text-primary pl-5 outline-none"
          placeholder={isReady ? "Search Place / postcode" : "Loading..."}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={!isReady}
        />
      </div>

      {predictions.length > 0 && (
        <ul className="space-y-3 text-sm bg-white absolute top-16 z-50 w-full shadow-lg rounded-md max-h-60 overflow-y-auto">
          {predictions.map((prediction) => (
            <li
              className="cursor-pointer p-2 hover:bg-primary/20 rounded-md"
              key={prediction.place_id}
              onClick={() =>
                handleSelect(prediction.place_id, prediction.description)
              }
            >
              {prediction.description}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};

export default PlaceAutocompleteForm;
